import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  View,
  Platform,
  Text,
  ActivityIndicator,
  SafeAreaView,
} from 'react-native';
import Modal from 'react-native-modal';
import HTML from 'react-native-render-html';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import { TextInputMask } from 'react-native-masked-text';
import { CheckBox, Icon } from 'react-native-elements';

import { ExploreProps } from '../../navigation';
import { RootState } from '../../store/rootReducer';
import SearchBar from '../../components/SearchBar';
import config from '../../lib/config';
import Logo from '../../components/Logo';
import { TouchableOpacity } from 'react-native-gesture-handler';
import {
  BottomSheetModal,
  BottomSheetModalProvider,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import { useDispatch, useSelector } from 'react-redux';
import TermAgeModal from '../TermModal';
import {
  fetchAgeGatedTerms,
  selectAgeGatedTerms,
} from '../../store/ageGatedCategoriesTerms';

const KEY = 'AGE_RESTRCITION_REMEMBER_EXPIRY';
const DATE_FORMAT = 'DD/MM/YYYY';

interface Props extends ExploreProps {
  age: null | number;
  children: null | any;
}

const dobRequired = 'Date of Birth is Required';

const AgeRestriction: React.FC<Props> = ({
  navigation,
  children,
  age,
}: any) => {
  const disaptch = useDispatch();
  const { content: termsContent, isLoading } = useSelector((state) =>
    selectAgeGatedTerms(state as RootState),
  );

  const [showScreen, setShowScreen] = useState(false);
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(false);
  const [accept, setAccept] = useState(false);

  const bottomSheetModalRef = useRef<BottomSheetModal>(null);
  const snapPoints = useMemo(() => ['100%'], []);

  const handleOpenTermsModal = useCallback(() => {
    // bottomSheetModalRef.current?.present();
    setShowTerms(true);
  }, []);

  const storeDate = useCallback(async (date) => {
    try {
      await AsyncStorage.setItem(KEY, date);
    } catch (e) {}
  }, []);

  const handleSubmit = useCallback(() => {
    setError('');

    if (dob.trim().length < 1) {
      setError(dobRequired);
      return;
    }

    if (dob.length !== 10 || !moment(dob, DATE_FORMAT).isValid()) {
      console.log('error');
      setError('Invalid date of birth.');
      return;
    }

    if (!accept) {
      setError('You must accept terms and conditions to continue.');
      return;
    }

    if (moment().subtract(age, 'years').diff(moment(dob, DATE_FORMAT)) < 0) {
      setError(`You must be ${age}+ to access this category.`);
      return;
    }

    if (remember) {
      storeDate(dob);
    } else {
      storeDate('');
    }

    setShowScreen(true);
  }, [dob, remember, setError, accept, setShowScreen, age, storeDate]);

  const handleChangeBottomSheet = useCallback(
    (index) => {
      if (!termsContent && !isLoading) {
        disaptch(fetchAgeGatedTerms());
      }
    },
    [termsContent, isLoading, disaptch],
  );

  useEffect(() => {
    if (!termsContent && !isLoading) {
      disaptch(fetchAgeGatedTerms());
    }
  }, [termsContent, isLoading, disaptch]);

  useEffect(() => {
    AsyncStorage.getItem(KEY).then((date) => {
      if (date) {
        setDob(date);
      }
    });
  }, [setDob]);

  const [showTerms, setShowTerms] = useState<boolean>(false);

  if (age === null || showScreen) {
    return children;
  }

  const onBackPressed = () => {
    navigation.goBack();
  };

  const onClose = () => {
    setShowTerms(false);
  };

  const inputTextStyles = [styles.textInput];
  let placeholderTextColor = '#303030';
  if (error === dobRequired) {
    placeholderTextColor = config.theme.brandColor;
  }

  const arrowSize = 26;
  const arrowName = 'arrow-forward';

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.keyboardView}
      enabled
    >
      <SearchBar
        placeholder="Search"
        enableBack
        onBackPressed={onBackPressed}
      />
      <ScrollView>
        <View style={styles.logoWrapper}>
          <Logo size={200} />
        </View>
        <Text style={styles.error}>{error}</Text>
        <View style={styles.card}>
          <Text style={styles.heading}>This is an Age Restricted Category</Text>
          <Text style={styles.subHeading}>Please Enter Your Date Of Birth</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputLeft} />
            <TextInputMask
              type={'datetime'}
              placeholder={DATE_FORMAT}
              options={{
                format: DATE_FORMAT,
              }}
              value={dob}
              onChangeText={setDob}
              style={inputTextStyles}
              placeholderTextColor={placeholderTextColor}
            />
          </View>
          <CheckBox
            title="Remember Me"
            checked={remember}
            onPress={() => setRemember((rememberMe) => !rememberMe)}
            containerStyle={styles.checkboxContainerStyle}
            textStyle={styles.checkboxTextStyle}
            iconType="ionicon"
            uncheckedIcon="square-outline"
            checkedIcon="checkbox-outline"
            checkedColor={config.theme.brandColor}
          />

          <View style={styles.termsContainer}>
            <CheckBox
              title={'I agree with the'}
              onPress={() => setAccept((acceptTerms) => !acceptTerms)}
              checked={accept}
              containerStyle={[
                styles.checkboxContainerStyle,
                styles.noMarginRight,
              ]}
              textStyle={[styles.checkboxTextStyle, styles.checkboxLayout]}
              iconType="ionicon"
              uncheckedIcon="square-outline"
              checkedIcon="checkbox-outline"
              checkedColor="#d83938"
            />

            <TouchableOpacity onPress={handleOpenTermsModal}>
              <Text style={styles.link}>Terms and Conditions</Text>
            </TouchableOpacity>
          </View>
          <View>
            <Icon
              tvParallaxProperties={null}
              name={arrowName}
              size={arrowSize}
              color={config.theme.advert.headingTextColor}
              onPress={handleSubmit}
              style={styles.submitButton}
              iconStyle={styles.iconBase}
            />
          </View>
        </View>
      </ScrollView>
      <TermAgeModal
        termsContent={termsContent}
        isLoading={isLoading}
        isVisible={showTerms}
        onClose={onClose}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
  },
  keyboardView: {
    flex: 1,
    backgroundColor: '#f7f7f7',
  },
  heading: {
    textAlign: 'center',
    fontSize: config.theme.headingFontSize,
    color: '#707070',
    fontWeight: config.theme.headingFontWeight,
    marginBottom: 4,
  },
  subHeading: {
    textAlign: 'center',
    color: '#707070',
    marginBottom: 20,
    marginTop: 5,
    fontSize: 20,
  },
  logoWrapper: {
    marginTop: 20,
  },
  card: {
    backgroundColor: 'white',
    marginHorizontal: 10,
    marginTop: 20,
    borderRadius: 10,
    padding: 10,
    marginBottom: 40,
  },
  inputLeft: {
    flex: 0.5,
    borderRightWidth: 0,
    borderRightColor: '#ccc',
  },
  inputWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    flexDirection: 'row',
    marginBottom: 12,
  },
  textInput: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    fontSize: 15,
    color: '#707070',
  },
  textInputError: {
    fontWeight: 'bold',
    color: config.theme.brandColor,
  },
  submitButton: {
    borderWidth: 1,
    borderColor: '#cccccc',
    borderRadius: 40,
    padding: 12,
    marginBottom: 16,
    alignSelf: 'center',
  },
  iconBase: {
    width: 24,
    height: 24,
  },
  checkboxContainerStyle: {
    backgroundColor: 'transparent',
    paddingVertical: 2,
    borderWidth: 0,
    marginBottom: 0,
    paddingRight: 0,
  },
  checkboxTextStyle: {
    color: '#707070',
    fontWeight: '400',
  },
  link: {
    color: '#3d5ed7',
    textDecorationLine: 'underline',
    marginTop: 5,
  },
  checkboxWrapper: {
    flexDirection: 'row',
    paddingLeft: 10,
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  termsContentContainer: {
    flexGrow: 1,
    paddingHorizontal: 15,
    paddingBottom: 20,
  },
  paragraph: {
    marginBottom: 15,
  },
  error: {
    color: config.theme.brandColor,
    textAlign: 'center',
    marginTop: 0,
    fontWeight: 'bold',
  },
  loaderContainer: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 20,
  },
  overlay: {
    margin: 0,
    marginTop: 40,
  },
  closeButtonContainer: {
    height: '100%',
    zIndex: 1000,
    flex: 1,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
    minHeight: '100%',
    paddingTop: 30,
    paddingHorizontal: 20,
  },
  loadingIndicator: {
    marginTop: 20,
  },
  noMarginRight: {
    marginRight: 0,
  },
  checkboxLayout: { marginRight: 3 },
});

export default AgeRestriction;
